import { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import { toast } from "react-toastify";
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./FOS.module.css";
import "../../components/mcrbTableTheme.css";

const { baseNodeapiUrl } = config;
const fosApiUrl = `${baseNodeapiUrl}/fos-api/fos`;

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyFos = {
    name: "",
    phone: "",
    email: "",
    city: "",
    status: true,
};

const AddFOS = () => {
    const params: any = useParams();
    const history = useHistory();
    const isEdit = Boolean(params?.id);

    const [fos, setFos] = useState<any>(emptyFos);
    const [referralCode, setReferralCode] = useState("");
    const [errors, setErrors] = useState({ name: "", phone: "", email: "" });
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    const fetchFos = async (id: any) => {
        setFetching(true);
        try {
            const { data } = await axios({
                url: `${fosApiUrl}/${id}`,
                method: "GET",
                headers: { "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}` },
            });
            if (data.status === 200) {
                setFos({
                    name: data?.data?.name || "",
                    phone: data?.data?.phone || "",
                    email: data?.data?.email || "",
                    city: data?.data?.city || "",
                    status: Boolean(data?.data?.status),
                });
                setReferralCode(data?.data?.referral_code || "");
            } else {
                toast.error(data.message || "FOS not found");
            }
        } catch (error) {
            console.log("error", error);
            toast.error("Something went wrong while fetching FOS");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (isEdit) {
            fetchFos(params.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.id]);

    function fieldChangeHandler(e: any) {
        const { name, value } = e.target;
        setFos((prev: any) => ({ ...prev, [name]: value }));
    }

    function fieldErrorFor(name: string, value: string) {
        if (name === "name") {
            return value ? "" : "This field is required !";
        }
        if (name === "phone") {
            if (!value) return "This field is required !";
            if (!PHONE_REGEX.test(value)) return "Enter a valid 10-digit mobile number !";
            return "";
        }
        if (name === "email") {
            if (!value) return "";
            if (!EMAIL_REGEX.test(value)) return "Enter a valid email address !";
            return "";
        }
        return "";
    }

    function errorHandler(e: any) {
        const { name, value } = e.target;
        setErrors((prev) => ({ ...prev, [name]: fieldErrorFor(name, value) }));
    }

    function validate() {
        const nextErrors = {
            name: fieldErrorFor("name", fos.name),
            phone: fieldErrorFor("phone", fos.phone),
            email: fieldErrorFor("email", fos.email),
        };
        setErrors(nextErrors);
        return !nextErrors.name && !nextErrors.phone && !nextErrors.email;
    }

    async function saveFos() {
        if (!validate()) {
            toast.error("Please fix the highlighted fields !");
            return;
        }
        setLoading(true);
        try {
            const payload: any = {
                name: fos.name,
                phone: fos.phone,
                email: fos.email,
                city: fos.city,
            };
            if (isEdit) payload.status = fos.status;

            const { data } = await axios({
                url: isEdit ? `${fosApiUrl}/${params.id}` : fosApiUrl,
                method: isEdit ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
                data: payload,
            });
            if (data.status === 200) {
                toast.success(isEdit ? "FOS updated successfully !" : "FOS created successfully !");
                history.push("/foslist");
            } else {
                toast.error(data.message || "Something went wrong !");
            }
        } catch (error: any) {
            console.log("error", error);
            toast.error(error?.response?.data?.message || "Something went wrong !");
        } finally {
            setLoading(false);
        }
    }

    function copyCode() {
        navigator.clipboard?.writeText(referralCode);
        toast.success("Referral code copied");
    }

    return (
        <ThemeMaker>
            <div className={`${styles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>{isEdit ? "Edit FOS" : "Add FOS"}</span>
                        <button type="button" className={brandStyles.primaryBtn} onClick={() => history.push("/foslist")}>
                            FOS List
                        </button>
                    </div>

                    <div className={brandStyles.bodyArea} style={{ minHeight: "60vh" }}>
                        {
                            fetching ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                                    <CircularProgress />
                                </div>
                            ) : (
                                <div className="row">
                                    {isEdit && referralCode ? (
                                        <div className="col-md-12">
                                            <div className={styles.referralBox}>
                                                <span>Referral Code: <span className={styles.referralCode}>{referralCode}</span></span>
                                                <div className="d-flex" style={{ gap: "8px" }}>
                                                    <button type="button" className={styles.regenerateBtn} onClick={copyCode}>
                                                        <ContentCopyIcon style={{ fontSize: 14 }} /> Copy
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>Name<span className={brandStyles.required}>*</span>
                                                {errors.name ? <span className={brandStyles.errorInline}>{errors.name}</span> : null}
                                            </label>
                                            <input type="text" className={brandStyles.input} name="name" placeholder="Enter name"
                                                value={fos.name} onChange={fieldChangeHandler} onBlur={errorHandler} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>Phone<span className={brandStyles.required}>*</span>
                                                {errors.phone ? <span className={brandStyles.errorInline}>{errors.phone}</span> : null}
                                            </label>
                                            <input type="text" className={brandStyles.input} name="phone" placeholder="10-digit mobile number"
                                                maxLength={10}
                                                value={fos.phone}
                                                onChange={(e) => {
                                                    const digitsOnly = e.target.value.replace(/\D/g, "");
                                                    setFos((prev: any) => ({ ...prev, phone: digitsOnly }));
                                                }}
                                                onBlur={errorHandler}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>Email
                                                {errors.email ? <span className={brandStyles.errorInline}>{errors.email}</span> : null}
                                            </label>
                                            <input type="email" className={brandStyles.input} name="email" placeholder="fos@example.com"
                                                value={fos.email} onChange={fieldChangeHandler} onBlur={errorHandler} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className={brandStyles.field}>
                                            <label className={brandStyles.label}>City</label>
                                            <input type="text" className={brandStyles.input} name="city" placeholder="Enter city"
                                                value={fos.city} onChange={fieldChangeHandler} />
                                        </div>
                                    </div>
                                    {isEdit ? (
                                        <div className="col-md-6">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Status</label>
                                                <div className={brandStyles.statusRow}>
                                                    <input type="checkbox" className={brandStyles.checkbox}
                                                        checked={fos.status}
                                                        onChange={() => setFos((prev: any) => ({ ...prev, status: !prev.status }))}
                                                    />
                                                    <span style={{ fontSize: "13px", color: "#6B7280", fontWeight: 600 }}>
                                                        {fos.status ? "Active" : "Inactive"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="col-md-12 mt-2">
                                        <button type="submit" className={isEdit ? brandStyles.updateBtn : brandStyles.saveBtn} disabled={loading} onClick={saveFos}>
                                            {loading ? "Saving..." : isEdit ? "Update" : "Save"}
                                        </button>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </ThemeMaker>
    );
};

export default AddFOS;
